"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Check, X } from "lucide-react";
import { APP_NAME } from "@/lib/brand";
import { Turnstile } from "@/components/auth/turnstile";

type UsernameStatus =
  | { state: "idle" }
  | { state: "checking" }
  | { state: "available" }
  | { state: "taken"; suggestions: string[] }
  | { state: "error"; message: string };

/** Returns yyyy-mm-dd for (today - years). */
function dateYearsAgo(years: number): string {
  const d = new Date();
  d.setFullYear(d.getFullYear() - years);
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${month}-${day}`;
}

/** True if the given yyyy-mm-dd is at least `years` old as of today. */
function isAtLeastYearsOld(dob: string, years: number): boolean {
  if (!dob) return false;
  const birth = new Date(dob);
  if (Number.isNaN(birth.getTime())) return false;
  const threshold = new Date();
  threshold.setFullYear(threshold.getFullYear() - years);
  return birth.getTime() <= threshold.getTime();
}

export default function SignupPage() {
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [token, setToken] = useState("");

  const [usernameStatus, setUsernameStatus] = useState<UsernameStatus>({
    state: "idle",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const maxDob = dateYearsAgo(13);
  const emailsMatch = email.length > 0 && email === confirmEmail;
  const emailMismatch = confirmEmail.length > 0 && email !== confirmEmail;
  const passwordMismatch =
    confirmPassword.length > 0 && password !== confirmPassword;

  async function handleCheckUsername() {
    if (!username.trim()) return;
    setUsernameStatus({ state: "checking" });
    try {
      const res = await fetch("/api/auth/check-username", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username: username.trim(),
          firstName: firstName.trim() || undefined,
          lastName: lastName.trim() || undefined,
          dateOfBirth: dateOfBirth || undefined,
        }),
      });
      const data: {
        available: boolean;
        suggestions?: string[];
        error?: string;
      } = await res.json();

      if (!res.ok) {
        setUsernameStatus({
          state: "error",
          message: data.error ?? "Could not check username.",
        });
        return;
      }

      if (data.available) {
        setUsernameStatus({ state: "available" });
      } else {
        setUsernameStatus({
          state: "taken",
          suggestions: data.suggestions ?? [],
        });
      }
    } catch {
      setUsernameStatus({ state: "error", message: "Could not check username." });
    }
  }

  function applySuggestion(suggestion: string) {
    setUsername(suggestion);
    setUsernameStatus({ state: "idle" });
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErrors({});

    const newErrors: Record<string, string> = {};
    if (!firstName.trim()) newErrors.firstName = "First name is required";
    if (!lastName.trim()) newErrors.lastName = "Last name is required";
    if (!dateOfBirth) newErrors.dateOfBirth = "Date of birth is required";
    else if (!isAtLeastYearsOld(dateOfBirth, 13))
      newErrors.dateOfBirth = "You must be at least 13 years old";
    if (!username.trim()) newErrors.username = "Username is required";
    if (!email.trim()) newErrors.email = "Email is required";
    if (email !== confirmEmail) newErrors.confirmEmail = "Emails do not match";
    if (password.length < 8)
      newErrors.password = "Must be at least 8 characters";
    else if (!/[A-Z]/.test(password))
      newErrors.password = "Must include an uppercase letter";
    else if (!/[0-9]/.test(password))
      newErrors.password = "Must include a number";
    if (password !== confirmPassword)
      newErrors.confirmPassword = "Passwords do not match";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsLoading(true);
    try {
      const res = await fetch("/api/auth/signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          dateOfBirth,
          username: username.trim(),
          email: email.trim(),
          confirmEmail: confirmEmail.trim(),
          password,
          turnstileToken: token,
        }),
      });
      const data: {
        error?: string;
        field?: string;
        suggestions?: string[];
      } = await res.json();

      if (!res.ok) {
        if (res.status === 409 && data.field === "username") {
          setErrors({ username: data.error ?? "Username is taken" });
          setUsernameStatus({
            state: "taken",
            suggestions: data.suggestions ?? [],
          });
          return;
        }
        setErrors({
          form: data.error ?? "Something went wrong. Please try again.",
        });
        return;
      }

      router.push(`/verify-email?email=${encodeURIComponent(email.trim())}`);
    } catch {
      setErrors({ form: "Something went wrong. Please try again." });
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <Card className="w-full">
      <CardHeader className="text-center">
        <CardTitle className="text-xl">Create Account</CardTitle>
        <CardDescription>Get started with {APP_NAME}</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="firstName">First Name</Label>
              <Input
                id="firstName"
                type="text"
                placeholder="Jane"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                disabled={isLoading}
              />
              {errors.firstName && (
                <p className="text-xs text-destructive">{errors.firstName}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName">Last Name</Label>
              <Input
                id="lastName"
                type="text"
                placeholder="Doe"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                disabled={isLoading}
              />
              {errors.lastName && (
                <p className="text-xs text-destructive">{errors.lastName}</p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="dateOfBirth">Date of Birth</Label>
            <Input
              id="dateOfBirth"
              type="date"
              max={maxDob}
              value={dateOfBirth}
              onChange={(e) => setDateOfBirth(e.target.value)}
              disabled={isLoading}
            />
            {errors.dateOfBirth && (
              <p className="text-xs text-destructive">{errors.dateOfBirth}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="username">Username</Label>
            <div className="flex gap-2">
              <Input
                id="username"
                type="text"
                placeholder="janedoe"
                value={username}
                onChange={(e) => {
                  setUsername(e.target.value);
                  setUsernameStatus({ state: "idle" });
                }}
                disabled={isLoading}
              />
              <Button
                type="button"
                variant="outline"
                onClick={handleCheckUsername}
                disabled={isLoading || !username.trim() || usernameStatus.state === "checking"}
              >
                {usernameStatus.state === "checking" ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  "Check availability"
                )}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Permanent — can&apos;t change later. 5–30 chars · lowercase letters,
              numbers, and . or _ (not at the start or end).
            </p>
            {usernameStatus.state === "available" && (
              <p className="flex items-center gap-1 text-xs text-emerald-500">
                <Check className="h-3 w-3" /> available
              </p>
            )}
            {usernameStatus.state === "taken" && (
              <div className="space-y-2">
                <p className="flex items-center gap-1 text-xs text-destructive">
                  <X className="h-3 w-3" /> taken
                </p>
                {usernameStatus.suggestions.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {usernameStatus.suggestions.map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => applySuggestion(s)}
                        className="rounded-full border border-input bg-background px-2.5 py-1 text-xs hover:bg-accent hover:text-accent-foreground"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {usernameStatus.state === "error" && (
              <p className="text-xs text-destructive">{usernameStatus.message}</p>
            )}
            {errors.username && (
              <p className="text-xs text-destructive">{errors.username}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="jane@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onPaste={(e) => e.preventDefault()}
              disabled={isLoading}
            />
            {errors.email && (
              <p className="text-xs text-destructive">{errors.email}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmEmail">Confirm Email</Label>
            <Input
              id="confirmEmail"
              type="email"
              placeholder="jane@company.com"
              value={confirmEmail}
              onChange={(e) => setConfirmEmail(e.target.value)}
              onPaste={(e) => e.preventDefault()}
              disabled={isLoading}
              aria-invalid={emailMismatch || undefined}
            />
            {emailMismatch && (
              <p className="text-xs text-destructive">Emails do not match</p>
            )}
            {errors.confirmEmail && !emailMismatch && (
              <p className="text-xs text-destructive">{errors.confirmEmail}</p>
            )}
            <p className="text-xs text-muted-foreground">
              After signup, check your inbox — and your spam folder — for the
              verification link.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              placeholder="Min 8 chars, 1 uppercase, 1 number"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
            />
            {errors.password && (
              <p className="text-xs text-destructive">{errors.password}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Confirm Password</Label>
            <Input
              id="confirmPassword"
              type="password"
              placeholder="Re-enter your password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={isLoading}
              aria-invalid={passwordMismatch || undefined}
            />
            {passwordMismatch && (
              <p className="text-xs text-destructive">Passwords do not match</p>
            )}
            {errors.confirmPassword && !passwordMismatch && (
              <p className="text-xs text-destructive">
                {errors.confirmPassword}
              </p>
            )}
          </div>

          <Turnstile onToken={setToken} />

          {errors.form && (
            <p className="text-sm text-destructive">{errors.form}</p>
          )}

          <Button
            type="submit"
            className="w-full"
            disabled={isLoading || (confirmEmail.length > 0 && !emailsMatch)}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating account...
              </>
            ) : (
              "Create Account"
            )}
          </Button>
        </form>
      </CardContent>
      <CardFooter className="justify-center">
        <p className="text-sm text-muted-foreground">
          Already have an account?{" "}
          <Link href="/login" className="text-primary hover:underline">
            Log in
          </Link>
        </p>
      </CardFooter>
    </Card>
  );
}
